import {
  IRevenueSchedulePeriod,
  IRevenueScheduleSubscriptionItem,
} from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import {
  AppColumnDef,
  createColumnHelper,
} from '../Monetize/DataTableNew/appTable';
import { FC, useMemo } from 'react';
import {
  buildServicePeriodIndex,
  formatProductType,
  formatRecognitionMethod,
  formatServicePeriod,
} from '../../utils/revenueSchedule';
import { MBox, MText } from '../Monetize';
import { DataTable } from '../Monetize/DataTableNew/DataTable';
import { OfferingLookupPopover } from '../Monetize/LookupPopover/OfferingLookupPopover';
import { ProductLookupPopover } from '../Monetize/LookupPopover/ProductLookupPopover';

interface RevenueContractSummaryTableProps {
  subscriptionItems: IRevenueScheduleSubscriptionItem[];
  periods: IRevenueSchedulePeriod[];
}

interface SummaryRow {
  rowId: string;
  offeringId: string;
  offeringName: string;
  productId: string;
  productName: string;
  productType: string;
  recognitionMethod: string;
  recognitionDuration?: number | null;
  serviceStart: string;
  serviceEnd: string;
  productAmount: number;
}

const PINNED_LEFT_COLUMNS = ['offeringName', 'productName'];
const PINNED_RIGHT_COLUMNS = ['productAmount'];

const COLUMN_DIVIDER_STYLES = {
  borderLeft: '1px solid var(--chakra-colors-gray-300)',
};

const NARROW_COLUMN_WIDTH = 120;

const columnHelper = createColumnHelper<SummaryRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildColumns = (): AppColumnDef<SummaryRow, any>[] => [
  columnHelper.accessor('offeringName', {
    header: 'Offering',
    size: NARROW_COLUMN_WIDTH,
    enableSorting: false,
    cell: (info) => {
      const { offeringId, offeringName } = info.row.original;
      const label = offeringName || offeringId || '—';
      if (!offeringId) {
        return <MText fontSize="xs">{label}</MText>;
      }
      return (
        <OfferingLookupPopover
          offeringId={offeringId}
          label={label}
          hideEditLink
        />
      );
    },
  }),
  columnHelper.accessor('productName', {
    header: 'Product',
    size: 170,
    enableSorting: false,
    meta: { cellStyles: COLUMN_DIVIDER_STYLES },
    cell: (info) => {
      const { productId, productName } = info.row.original;
      const label = productName || productId || '—';
      if (!productId) {
        return <MText fontSize="xs">{label}</MText>;
      }
      return (
        <ProductLookupPopover
          productId={productId}
          label={label}
          hideEditLink
        />
      );
    },
  }),
  columnHelper.accessor('productType', {
    header: 'Product Type',
    size: NARROW_COLUMN_WIDTH,
    enableSorting: false,
    meta: { cellStyles: COLUMN_DIVIDER_STYLES },
    cell: (info) => (
      <MText fontSize="xs">
        {formatProductType(info.row.original.productType)}
      </MText>
    ),
  }),
  columnHelper.display({
    id: 'recognitionMethod',
    header: 'Recognition Method',
    size: 180,
    enableSorting: false,
    meta: { cellStyles: COLUMN_DIVIDER_STYLES },
    cell: (info) => {
      const { recognitionMethod, recognitionDuration } = info.row.original;
      return (
        <MText fontSize="xs">
          {formatRecognitionMethod(recognitionMethod, recognitionDuration)}
        </MText>
      );
    },
  }),
  columnHelper.display({
    id: 'servicePeriod',
    header: 'Service Period',
    size: 200,
    enableSorting: false,
    meta: { cellStyles: COLUMN_DIVIDER_STYLES },
    cell: (info) => {
      const { serviceStart, serviceEnd } = info.row.original;
      return (
        <MText fontSize="xs">
          {formatServicePeriod({ start: serviceStart, end: serviceEnd })}
        </MText>
      );
    },
  }),
  columnHelper.accessor('productAmount', {
    header: 'Sales Amount',
    size: NARROW_COLUMN_WIDTH,
    enableSorting: false,
    meta: { cellStyles: COLUMN_DIVIDER_STYLES },
    cell: (info) => (
      <MText fontWeight="bold" fontSize="xs">
        {formatCurrency(info.row.original.productAmount)}
      </MText>
    ),
  }),
];

export const RevenueContractSummaryTable: FC<
  RevenueContractSummaryTableProps
> = ({ subscriptionItems, periods }) => {
  const servicePeriodIndex = useMemo(
    () => buildServicePeriodIndex(periods),
    [periods],
  );

  const rows = useMemo<SummaryRow[]>(() => {
    if (subscriptionItems.length > 0) {
      return subscriptionItems.map((item) => {
        const range = servicePeriodIndex.get(item.subscriptionItemId);
        return {
          rowId: item.subscriptionItemId,
          offeringId: item.references.offeringId ?? '',
          offeringName: item.offeringName ?? '',
          productId: item.references.productId ?? '',
          productName: item.productName ?? '',
          productType: item.productType ?? '',
          recognitionMethod: item.recognitionMethod ?? '',
          recognitionDuration: item.recognitionDuration,
          serviceStart: range?.start ?? '',
          serviceEnd: range?.end ?? '',
          productAmount: item.productAmount ?? 0,
        };
      });
    }
    // Fall back to grouping periods by row (mirrors RevenueScheduleTable) when
    // the subscription-items endpoint returns nothing but schedule periods
    // exist, so the Summary still renders instead of "No offerings found".
    const byKey = new Map<string, SummaryRow>();
    for (const period of periods) {
      const key = period.references.subscriptionItemId || period.id;
      if (byKey.has(key)) {
        continue;
      }
      const range = servicePeriodIndex.get(key);
      byKey.set(key, {
        rowId: key,
        offeringId: period.references.offeringId ?? '',
        offeringName: period.offeringName ?? '',
        productId: period.references.productId ?? '',
        productName: period.productName ?? '',
        productType: period.productType ?? '',
        recognitionMethod: period.recognitionMethod ?? '',
        recognitionDuration: period.recognitionDuration,
        serviceStart: range?.start ?? '',
        serviceEnd: range?.end ?? '',
        productAmount: period.productAmount ?? 0,
      });
    }
    return Array.from(byKey.values());
  }, [subscriptionItems, periods, servicePeriodIndex]);

  const columns = useMemo(() => buildColumns(), []);

  if (!rows.length) {
    return (
      <MBox py={4}>
        <MText color="tGray.darkPurple" fontSize="sm">
          No offerings or products found.
        </MText>
      </MBox>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={rows}
      enablePagination={false}
      clientControlled
      skipFilterValuePreprocessing
      pinnedLeftColumns={PINNED_LEFT_COLUMNS}
      pinnedRightColumns={PINNED_RIGHT_COLUMNS}
      tableProps={{
        bodyRow: {
          cursor: 'default',
          _hover: { backgroundColor: 'inherit' },
        },
      }}
      getRowId={(row) => (row as SummaryRow).rowId}
    />
  );
};
